import {createContext, useCallback, useEffect, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {useNavigate} from "react-router-dom";
import {toast} from "react-toastify";
import axiosInstance from "../utils/axiosInstance";
import refetchToken from "../utils/refetchToken";
import {clearStoredAuth, persistAuthTokens, readStoredAuthTokens} from "../utils/authStorage";


const getUser = async () => {
    const { data } = await axiosInstance.get(`/api/v1/user/`);
    return data;
};

const AuthContext = createContext('')

export default AuthContext

export const applyAuthorizationHeader = (token) => {
    if (token) {
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
        delete axiosInstance.defaults.headers.common['Authorization'];
    }
};

const getInitialAuthState = () => {
    const {accessToken, refreshToken} = readStoredAuthTokens();
    return {accessToken, refreshToken};
};

export const AuthProvider = ({children}) => {
    const navigate = useNavigate();
    const [initialAuthState] = useState(getInitialAuthState);

    const [accessToken, setAccessToken] = useState(initialAuthState.accessToken)
    const [refreshToken, setRefreshToken] = useState(initialAuthState.refreshToken)
    const [user, setUser] = useState(null)
    // useAuth(true, (data) => {
    //         setUser(data?.results[0])
    // })

    const setAuthTokens = useCallback(({accessToken: newAccessToken, refreshToken: newRefreshToken}) => {
        persistAuthTokens({accessToken: newAccessToken, refreshToken: newRefreshToken});
        applyAuthorizationHeader(newAccessToken);
        setAccessToken(newAccessToken || '');
        setRefreshToken(newRefreshToken || '');
    }, []);

    const logout = useCallback(({redirect = true, message = ''} = {}) => {
        clearStoredAuth();
        applyAuthorizationHeader('');
        setAccessToken('');
        setRefreshToken('');
        setUser(null);

        if (message) {
            toast.success(message);
        }

        if (redirect) {
            navigate('/login', {replace: true});
        }
    }, [navigate]);

    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'accessToken' && e.newValue === null) {
                logout()
            }
        }
        window.addEventListener('storage', handleStorageChange)
        return () => window.removeEventListener('storage', handleStorageChange)
    }, [logout])

    const {data: userData, isLoading, isFetching} = useQuery({
        queryKey: ["user"],
        queryFn: async () => {
            try {
                return await getUser();
            } catch (error) {
                if (error?.response?.status === 401 && refreshToken) {
                    try {
                        const response = await refetchToken(refreshToken);
                        const newAccessToken = response?.data?.access;

                        if (!newAccessToken) {
                            throw new Error('Token refresh did not return an access token.');
                        }

                        setAuthTokens({accessToken: newAccessToken, refreshToken});
                        return await getUser();
                    } catch (refreshError) {
                        logout();
                        throw refreshError;
                    }
                }

                logout();
                throw error;
            }
        },
        enabled: Boolean(accessToken),
        retry: false,
        refetchOnWindowFocus: false,
        onSuccess: (data) => {
            setUser(data?.results?.[0] || null)
        },
    })

    // const { status, data, error, refetch } = useQuery({
    //     queryKey: ['refreshToken'],
    //     queryFn: () => refetchToken(refreshToken),
    //     keepPreviousData : true,
    //     refetchInterval: 4000,
    //     // retry: false,
    //     onSuccess: () => {
    //         if(status === 'success'){
    //             console.log("refreshToken success", data.data.access)
    //             setAccessToken(data.data.access)
    //             localStorage.setItem('accessToken', JSON.stringify(data.data.access))
    //             axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${data.data.access}`
    //         }
    //     },
    //     onError: () => console.log("refreshToken error", error)
    // })


    const authenticatedUser = user || userData?.results?.[0] || null;

    let contextData = {
        user: authenticatedUser,
        setUser,
        accessToken,
        setAccessToken,
        refreshToken,
        setRefreshToken,
        setAuthTokens,
        logout,
        isUserLoading: Boolean(accessToken) && (isLoading || isFetching)
    }

    // useEffect(()=> {
    //
    //     if(authTokens){
    //         setUser(jwt_decode(authTokens.access))
    //     }
    //     setLoading(false)
    //
    //
    // }, [authTokens, loading])

    return(
        <AuthContext.Provider value={contextData}>
            {children}
        </AuthContext.Provider>
    )
}
